
import { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Check, Loader2, AlertCircle, Search } from 'lucide-react'
import { Body } from '@meiuca/ui-components'
import { modalitiesManager } from '@/managers/modalitiesManager'
import type { SportModality } from '@/types/address'

interface SportModalitySelectorProps {
  selectedModalities: SportModality[]
  onModalitiesChange: (modalities: SportModality[]) => void
  error?: string
}

export function SportModalitySelector({ 
  selectedModalities, 
  onModalitiesChange, 
  error 
}: SportModalitySelectorProps) {
  const [modalities, setModalities] = useState<SportModality[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const loadModalities = async () => {
      try {
        setIsLoading(true)
        setLoadError(null)

        // Use modalitiesManager (singleton with cache)
        const modalitiesList = await modalitiesManager.getModalities()

        // Convert Modality[] to SportModality[]
        const sportModalities: SportModality[] = modalitiesList.map(m => ({
          id: m.id,
          name: m.description
        }))

        setModalities(sportModalities)
      } catch (error: any) {
        setLoadError(error.message)
        console.error('Erro ao carregar modalidades:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadModalities()
  }, [])

  // Função para normalizar texto (remover acentos e converter para lowercase)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
  }

  // Filtrar modalidades com base na busca
  const filteredModalities = useMemo(() => {
    if (!searchQuery.trim()) return modalities

    const normalizedQuery = normalizeText(searchQuery)
    return modalities.filter(modality =>
      normalizeText(modality.name).includes(normalizedQuery)
    )
  }, [modalities, searchQuery])

  const toggleModality = (modality: SportModality) => {
    const isSelected = selectedModalities.some(m => m.id === modality.id)
    
    if (isSelected) {
      // Remove modality
      onModalitiesChange(
        selectedModalities.filter(m => m.id !== modality.id)
      )
    } else {
      // Add modality
      onModalitiesChange([...selectedModalities, modality])
    }
  }

  const isSelected = (modalityId: string) => {
    return selectedModalities.some(m => m.id === modalityId)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <label className="text-sm font-medium text-text-primary">
          Esportes que você pratica
        </label>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
          <Body className="ml-2 text-text-secondary">
            Carregando modalidades...
          </Body>
        </div>
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <label className="text-sm font-medium text-text-primary">
          Esportes que você pratica
        </label>
        <div className="p-4 bg-background-negative border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-negative flex-shrink-0" />
          <Body className="text-negative">{loadError}</Body>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary">
          Esportes que você pratica
        </label>
        <Body className="text-text-secondary text-sm">
          Selecione todas as modalidades esportivas que você pratica ou tem interesse
        </Body>
      </div>

      {/* Search Field */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-4 h-4" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar esporte..."
          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary transition-colors"
        />
      </div>

      {/* Modalities Grid with Fixed Height */}
      <div className="h-80 overflow-y-auto border border-gray-200 rounded-lg p-4">
        {filteredModalities.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
              <Body className="text-text-secondary">
                {searchQuery ? 'Nenhum esporte encontrado' : 'Nenhum esporte disponível'}
              </Body>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filteredModalities.map((modality, index) => {
          const selected = isSelected(modality.id)
          
          return (
            <motion.button
              key={modality.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => toggleModality(modality)}
              className={`
                relative p-4 rounded-lg border-2 transition-all duration-200 text-left
                ${selected 
                  ? 'border-brand-primary bg-brand-primary/5 text-brand-primary' 
                  : 'border-gray-200 bg-background-white hover:border-gray-300 text-text-primary'
                }
              `}
            >
              {/* Selection Indicator */}
              <div className="flex items-center justify-between mb-2">
                <div className={`
                  w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                  ${selected 
                    ? 'border-brand-primary bg-brand-primary' 
                    : 'border-gray-300'
                  }
                `}>
                  {selected && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>

              {/* Modality Name */}
              <div className="font-medium text-sm leading-tight">
                {modality.name}
              </div>

              {/* Selection Overlay */}
              {selected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute inset-0 border-2 border-brand-primary rounded-lg pointer-events-none"
                />
              )}
            </motion.button>
          )
            })}
          </div>
        )}
      </div>

      {/* Selection Counter */}
      {selectedModalities.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-3 bg-brand-primary/5 border border-brand-primary/20 rounded-lg"
        >
          <Body className="text-brand-primary text-sm">
            {selectedModalities.length} modalidade{selectedModalities.length !== 1 ? 's' : ''} selecionada{selectedModalities.length !== 1 ? 's' : ''}
          </Body>
        </motion.div>
      )}

      {error && (
        <Body className="text-negative text-sm">{error}</Body>
      )}
    </div>
  )
}